import type { ActorEvent, CustomViewConfig, Pagination, RecordLikeProtocol, RichTextContentProtocol } from '@lighthouse/core'
import { ARRANGEMENT, BLOCK_SIZE, CUSTOM_VIEW_LAYOUT, DIRECTION } from '@lighthouse/core'
import type { ContainerLayoutContextValue, FlowLayoutNode } from '@lighthouse/shared'
import { ContainerLayoutProvider, useContainerLayoutContext, ViewEmptyState } from '@lighthouse/shared'
import { BreakPointSize } from '@lighthouse/tools'
import React, { forwardRef, memo, useMemo } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { CarouselView } from './CarouselView'
import { HorizontalView } from './HorizontalView'
import { VerticalView } from './VerticalView'

const CUSTOM_VIEW_LAYOUTS = new Set(Object.values(CUSTOM_VIEW_LAYOUT))

interface CustomViewBlockProps {
    node: FlowLayoutNode
    blockWidth: number
    breakPoint?: BreakPointSize
    readonly?: boolean
    scale?: number
    pagination: Pagination
    onRenderRichText: (v: RichTextContentProtocol) => string
    onLoadMoreData?: (pageNum: number) => Promise<RecordLikeProtocol[]>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onUpdateRecord?: (recordId: string, content: RecordLikeProtocol['content']) => Promise<RecordLikeProtocol>
}

export const CustomViewBlock = memo(
    forwardRef<HTMLDivElement, CustomViewBlockProps>((props, ref) => {
        const {
            node,
            blockWidth,
            breakPoint,
            readonly,
            scale,
            pagination,
            onRenderRichText,
            onLoadMoreData,
            onRecordClick,
            onUpdateRecord
        } = props
        const { blockData, recordPoints, dataSource } = useViewBlockContext()
        const { id, title, config } = blockData
        const { breakPoint: baseBreakPointConfig, canPaginate, canDragViewSort, viewSortConfig, anchor } = config as CustomViewConfig
        const { anchorId } = anchor || {}
        const effectiveAnchorId = useEffectiveAnchorId(anchorId)
        useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
        const showEmpty = !dataSource || recordPoints.length === 0
        const { size: viewSize, viewLayout, emptyView } = baseBreakPointConfig
        const viewSortId = viewSortConfig?.sortId

        const enableDrag = canDragViewSort && Boolean(viewSortId)
        const {
            layout = CUSTOM_VIEW_LAYOUT.waterfall,
            arrangement = ARRANGEMENT.auto,
            cols,
            size = BLOCK_SIZE.md,
            gap,
            carousel,
            direction: viewDirection = DIRECTION.vertical,
            wrap = false
        } = viewLayout || {}

        const gapPx = gap?.size ?? 0

        const autoCols = useMemo(() => {
            const xLargeSize = new Map([
                [BreakPointSize.lg, 2],
                [BreakPointSize.md, 1],
                [BreakPointSize.sm, 1],
                [BreakPointSize.xs, 1]
            ])
            const largeSize = new Map([
                [BreakPointSize.lg, 3],
                [BreakPointSize.md, 2],
                [BreakPointSize.sm, 1],
                [BreakPointSize.xs, 1]
            ])
            const middleSize = new Map([
                [BreakPointSize.lg, 4],
                [BreakPointSize.md, 3],
                [BreakPointSize.sm, 2],
                [BreakPointSize.xs, 1]
            ])
            const smallSize = new Map([
                [BreakPointSize.lg, 5],
                [BreakPointSize.md, 4],
                [BreakPointSize.sm, 3],
                [BreakPointSize.xs, 2]
            ])

            const sizeMap = new Map([
                [BLOCK_SIZE.xl, xLargeSize],
                [BLOCK_SIZE.lg, largeSize],
                [BLOCK_SIZE.md, middleSize],
                [BLOCK_SIZE.sm, smallSize]
            ])

            return (breakPoint && sizeMap.get(size)?.get(breakPoint)) ?? 3
        }, [breakPoint, size])

        const safeLayout = CUSTOM_VIEW_LAYOUTS.has(layout) ? layout : CUSTOM_VIEW_LAYOUT.grid
        const isHorizontalGrid = safeLayout === CUSTOM_VIEW_LAYOUT.grid && viewDirection === DIRECTION.horizontal
        const finalCols = arrangement === ARRANGEMENT.fixed && cols ? cols : isHorizontalGrid && !wrap ? autoCols + 0.3 : autoCols

        const itemWidth = useMemo(() => {
            return (blockWidth - (finalCols - 1) * gapPx) / finalCols
        }, [blockWidth, finalCols, gapPx])

        const { level } = useContainerLayoutContext()

        const contextValue: ContainerLayoutContextValue = useMemo(() => {
            return {
                parentId: id,
                level: level + 1,
                size: {
                    width: {
                        size: blockWidth,
                        unit: 'px'
                    },
                    height: {
                        size: 'auto'
                    }
                },
                layout: {
                    ...baseBreakPointConfig.layout
                }
            }
        }, [id, level, blockWidth, baseBreakPointConfig.layout])

        if (showEmpty) {
            return <ViewEmptyState id={effectiveAnchorId} config={emptyView} onRenderRichText={onRenderRichText} />
        }

        return (
            <ContainerLayoutProvider value={contextValue}>
                {safeLayout === CUSTOM_VIEW_LAYOUT.carousel ? (
                    <CarouselView
                        ref={ref}
                        anchorId={effectiveAnchorId}
                        node={node}
                        title={title}
                        readonly={readonly}
                        options={carousel}
                        onRecordClick={onRecordClick}
                    />
                ) : isHorizontalGrid ? (
                    <HorizontalView
                        ref={ref}
                        node={node}
                        anchorId={effectiveAnchorId}
                        wrap={wrap}
                        arrangement={viewSize?.width?.size === 'auto' ? ARRANGEMENT.fit : arrangement}
                        itemWidth={itemWidth}
                        title={title}
                        cols={finalCols}
                        gap={gapPx}
                        readonly={readonly}
                        canPaginate={canPaginate}
                        pagination={pagination}
                        scale={scale}
                        enableDrag={enableDrag}
                        sortId={viewSortId}
                        onLoadMoreData={onLoadMoreData}
                        onRecordClick={onRecordClick}
                        onUpdateRecord={onUpdateRecord}
                        style={{
                            position: 'relative',
                            // 由于overflow: hidden重新生成上下文了，需要设置zIndex盖住自定义视图的遮罩层
                            zIndex: 2
                        }}
                    />
                ) : (
                    <VerticalView
                        ref={ref}
                        node={node}
                        anchorId={effectiveAnchorId}
                        itemWidth={itemWidth}
                        title={title}
                        cols={finalCols}
                        gap={gapPx}
                        arrangement={viewSize?.width?.size === 'auto' ? ARRANGEMENT.fit : arrangement}
                        layout={safeLayout}
                        readonly={readonly}
                        scale={scale}
                        enableDrag={enableDrag}
                        sortId={viewSortId}
                        onRecordClick={onRecordClick}
                        onUpdateRecord={onUpdateRecord}
                    />
                )}
            </ContainerLayoutProvider>
        )
    })
)
